import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLViewport = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Responsive Design</div>
                <h1>Viewport Meta Tag & Mobile Optimization</h1>
                <p className="hero-lead">
                    Master the viewport meta tag for mobile-responsive websites. Learn how to control
                    layout on mobile devices and create websites that look perfect on any screen size.
                </p>
            </div>

            <div className="content-section">
                <h2>What is the Viewport?</h2>
                <p>
                    The viewport is the user's visible area of a web page. On mobile devices, without
                    proper viewport configuration, browsers render pages at desktop width and scale down.
                </p>

                <div className="comparison-grid">
                    <div className="comparison-card error">
                        <h3> Without Viewport Meta Tag</h3>
                        <ul>
                            <li>Page rendered at ~980px width</li>
                            <li>Zoomed out to fit screen</li>
                            <li>Text too small to read</li>
                            <li>User must pinch-zoom</li>
                        </ul>
                    </div>
                    <div className="comparison-card">
                        <h3>With Viewport Meta Tag</h3>
                        <ul>
                            <li>Page width matches device</li>
                            <li>Proper scaling</li>
                            <li>Readable text size</li>
                            <li>No zooming needed</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>The Essential Viewport Meta Tag</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    
    <!-- Essential viewport meta tag --&gt;
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    
    <title>Responsive Page</title>
</head>
<body>
    <!-- Content --&gt;
</body>
</html>`} />
                </div>

                <h3>What It Does:</h3>
                <ul>
                    <li><code>width=device-width</code> - Sets page width to device's screen width</li>
                    <li><code>initial-scale=1.0</code> - Sets initial zoom level to 100% (no zoom)</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Viewport Properties</h2>

                <table className="reference-table">
                    <thead>
                        <tr>
                            <th>Property</th>
                            <th>Description</th>
                            <th>Values</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><code>width</code></td>
                            <td>Viewport width</td>
                            <td><code>device-width</code> or number (px)</td>
                        </tr>
                        <tr>
                            <td><code>height</code></td>
                            <td>Viewport height</td>
                            <td><code>device-height</code> or number (px)</td>
                        </tr>
                        <tr>
                            <td><code>initial-scale</code></td>
                            <td>Initial zoom level</td>
                            <td>0.1 to 10</td>
                        </tr>
                        <tr>
                            <td><code>minimum-scale</code></td>
                            <td>Minimum zoom level</td>
                            <td>0.1 to 10</td>
                        </tr>
                        <tr>
                            <td><code>maximum-scale</code></td>
                            <td>Maximum zoom level</td>
                            <td>0.1 to 10</td>
                        </tr>
                        <tr>
                            <td><code>user-scalable</code></td>
                            <td>Allow user zooming</td>
                            <td><code>yes</code> or <code>no</code></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="content-section">
                <h2>Common Viewport Configurations</h2>

                <h3>Standard Responsive (Recommended)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<meta name="viewport" content="width=device-width, initial-scale=1.0">

<!-- This is the most common and recommended configuration --&gt;`} />
                </div>

                <h3>Prevent Zoom (Not Recommended)</h3>
                <div className="code-example error">
                    <CodeBlock language="html" code={`<meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">

<!--  Accessibility issue: prevents users from zooming
     Only use for web apps where pinch-zoom conflicts with gestures --&gt;`} />
                </div>

                <h3>Allow Limited Zoom</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<meta name="viewport" content="width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=3.0">

<!-- Allows zoom up to 3x --&gt;`} />
                </div>

                <h3>Fixed Width (Not Responsive)</h3>
                <div className="code-example error">
                    <CodeBlock language="html" code={`<meta name="viewport" content="width=1024">

<!--  Forces 1024px width, not responsive --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Testing Without Viewport Meta Tag</h2>

                <div className="code-example error">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html>
<head>
    <title>No Viewport Meta Tag</title>
    <!-- Missing: <meta name="viewport" content="width=device-width, initial-scale=1.0"> --&gt;
    <style>
        body { font-size: 16px; }
        .box { width: 300px; padding: 20px; background: #4CAF50; }
    </style>
</head>
<body>
    <div class="box">
        This text will be tiny on mobile devices!
    </div>
</body>
</html>

<!-- Result on mobile:
   - Renders at ~980px width
   - Zoomed out to fit screen
   - Text barely readable --&gt;`} />
                </div>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html>
<head>
    <title>With Viewport Meta Tag</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
        body { font-size: 16px; }
        .box { width: 300px; padding: 20px; background: #4CAF50; }
    </style>
</head>
<body>
    <div class="box">
        This text is perfectly readable on mobile!
    </div>
</body>
</html>

<!-- Result on mobile:
   - Renders at device width
   - Proper scale
   - Text readable without zooming --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Responsive Design with Media Queries</h2>
                <p>Viewport meta tag works with CSS media queries for responsive layouts:</p>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html>
<head>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <style>
        .container {
            padding: 20px;
            background: #f5f5f5;
        }
        
        /* Mobile first (default) */
        .column {
            width: 100%;
            padding: 10px;
            background: #4CAF50;
            margin: 10px 0;
        }
        
        /* Tablet */
        @media (min-width: 768px) {
            .column {
                width: 48%;
                display: inline-block;
            }
        }
        
        /* Desktop */
        @media (min-width: 1024px) {
            .column {
                width: 30%;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="column">Column 1</div>
        <div class="column">Column 2</div>
        <div class="column">Column 3</div>
    </div>
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Mobile-Specific Considerations</h2>

                <h3>Touch-Friendly Elements</h3>
                <div className="code-example">
                    <CodeBlock language="css" code={`<style>
/* Minimum touch target size: 48x48px */
button, a {
    min-height: 48px;
    min-width: 48px;
    padding: 12px 24px;
    
    /* Prevent text selection during touch */
    -webkit-tap-highlight-color: transparent;
    user-select: none;
}

/* Spacing between clickable elements */
.button-group button {
    margin: 8px;
}
</style>`} />
                </div>

                <h3>Prevent Zoom on Input Focus (iOS)</h3>
                <div className="code-example">
                    <CodeBlock language="css" code={`<style>
/* iOS zooms in on inputs with font-size < 16px */
/* Solution: Use 16px or larger */
input, select, textarea {
    font-size: 16px; /* Prevents auto-zoom on iOS */
}
</style>`} />
                </div>

                <h3>Safe Area for Notched Devices</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover">

<style>
/* Add padding for notch/safe area */
body {
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
}
</style>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Debugging Viewport Issues</h2>

                <h3>Check Viewport in JavaScript</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`// Get viewport dimensions
const viewportWidth = window.innerWidth;
const viewportHeight = window.innerHeight;

console.log('Viewport:', viewportWidth, 'x', viewportHeight);

// Get device pixel ratio
const pixelRatio = window.devicePixelRatio;
console.log('Pixel ratio:', pixelRatio);

// Detect orientation
const orientation = window.innerWidth &gt; window.innerHeight ? 'landscape' : 'portrait';
console.log('Orientation:', orientation);

// Listen for viewport changes
window.addEventListener('resize', () => {
    console.log('Viewport resized:', window.innerWidth, 'x', window.innerHeight);
});

// Detect mobile device
const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
console.log('Is mobile:', isMobile);`} />
                </div>

                <h3>Chrome DevTools Mobile Testing</h3>
                <ol>
                    <li>Open DevTools (F12)</li>
                    <li>Click "Toggle device toolbar" (Ctrl+Shift+M)</li>
                    <li>Select device preset or custom dimensions</li>
                    <li>Test different screen sizes and orientations</li>
                    <li>Throttle network to simulate mobile speeds</li>
                </ol>
            </div>

            <div className="content-section">
                <h2>Common Viewport Problems & Solutions</h2>

                <div className="problems-list">
                    <h3>Problem 1: Horizontal Scrollbar on Mobile</h3>
                    <div className="code-example error">
                        <CodeBlock language="html" code={`<!--  Causes horizontal scroll --&gt;
<div style="width: 1200px;">
    Too wide for mobile!
</div>`} />
                    </div>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<!-- Responsive width --&gt;
<div style="max-width: 100%; width: 1200px;">
    Fits mobile screens!
</div>

<style>
/* Or use this globally */
* {
    max-width: 100%;
}
</style>`} />
                    </div>

                    <h3>Problem 2: Text Too Small on Mobile</h3>
                    <div className="code-example error">
                        <CodeBlock language="html" code={`<!--  Fixed small font --&gt;
<style>
body { font-size: 12px; }
</style>`} />
                    </div>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<!-- Responsive font sizes --&gt;
<style>
body { 
    font-size: 16px; /* Base size */
}

@media (min-width: 768px) {
    body { font-size: 18px; }
}

/* Or use viewport units */
h1 { font-size: calc(24px + 2vw); }
</style>`} />
                    </div>

                    <h3>Problem 3: Images Breaking Layout</h3>
                    <div className="code-example error">
                        <CodeBlock language="html" code={`<!--  Fixed width image --&gt;
<img src="photo.jpg" width="800">`} />
                    </div>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<!-- Responsive image --&gt;
<img src="photo.jpg" style="max-width: 100%; height: auto;">

<style>
/* Apply to all images */
img {
    max-width: 100%;
    height: auto;
}
</style>`} />
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Complete Responsive Template</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Responsive Page</title>
    <style>
        * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
        }
        
        body {
            font-family: Arial, sans-serif;
            font-size: 16px;
            line-height: 1.6;
        }
        
        img {
            max-width: 100%;
            height: auto;
        }
        
        .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
        }
        
        /* Mobile first */
        .column {
            width: 100%;
            padding: 15px;
        }
        
        /* Tablet */
        @media (min-width: 768px) {
            .column {
                width: 50%;
                float: left;
            }
        }
        
        /* Desktop */
        @media (min-width: 1024px) {
            .column {
                width: 33.33%;
            }
        }
        
        /* Touch-friendly buttons */
        button {
            min-height: 48px;
            min-width: 48px;
            padding: 12px 24px;
            font-size: 16px;
            cursor: pointer;
        }
    </style>
</head>
<body>
    <div class="container">
        <h1>Responsive Layout</h1>
        <div class="column">Column 1</div>
        <div class="column">Column 2</div>
        <div class="column">Column 3</div>
    </div>
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>

                <div className="checklist">
                    <ul>
                        <li> Always include viewport meta tag</li>
                        <li> Use <code>width=device-width, initial-scale=1.0</code></li>
                        <li> Don't disable user zooming (accessibility)</li>
                        <li> Use responsive units (%, vw, vh, rem)</li>
                        <li> Make touch targets at least 48x48px</li>
                        <li> Use mobile-first CSS approach</li>
                        <li> Test on real devices</li>
                        <li> Optimize images for mobile</li>
                        <li> Use 16px+ font size for inputs (prevent iOS zoom)</li>
                        <li> Consider safe areas for notched devices</li>
                    </ul>
                </div>
            </div>

        </div>
    );
};

export default HTMLViewport;



